import type { MacroTimer, MacroTimerState } from './types';
import { DEFAULT_BREAK_MS, DEFAULT_FOCUS_MS } from './types';

export function createMacroTimer(
  focusMs: number = DEFAULT_FOCUS_MS,
  breakMs: number = DEFAULT_BREAK_MS,
  openEnded: boolean = false
): MacroTimer {
  return {
    state: 'inactive',
    focusDurationMs: focusMs,
    breakDurationMs: breakMs,
    openEnded,
    readingDurationMs: 0,
    focusStartedAt: null,
    breakStartedAt: null,
    focusAccumulatedMs: 0,
    breakAccumulatedMs: 0,
    readingAccumulatedMs: 0,
    distractions: 0,
    pausedAt: null
  };
}

export interface TickResult {
  timer: MacroTimer;
  transitionedTo: MacroTimerState | null;
}

function now(): number {
  return Date.now();
}

export function startFocus(timer: MacroTimer): MacroTimer {
  if (
    timer.state !== 'inactive' &&
    timer.state !== 'pending_finish' &&
    timer.state !== 'break_running' &&
    timer.state !== 'break_paused'
  ) {
    return timer;
  }
  return {
    ...timer,
    state: 'focus_running',
    focusStartedAt: now(),
    breakStartedAt: null,
    pausedAt: null
  };
}

export function pauseFocus(timer: MacroTimer): MacroTimer {
  if (timer.state !== 'focus_running') return timer;
  const t = now();
  const delta = timer.focusStartedAt ? t - timer.focusStartedAt : 0;
  return {
    ...timer,
    state: 'focus_paused',
    focusAccumulatedMs: timer.focusAccumulatedMs + delta,
    focusStartedAt: null,
    pausedAt: t
  };
}

export function resumeFocus(timer: MacroTimer): MacroTimer {
  if (timer.state !== 'focus_paused') return timer;
  return {
    ...timer,
    state: 'focus_running',
    focusStartedAt: now(),
    pausedAt: null
  };
}

export function startBreak(timer: MacroTimer): MacroTimer {
  return {
    ...timer,
    state: 'break_running',
    focusStartedAt: null,
    breakStartedAt: now(),
    pausedAt: null
  };
}

export function pauseBreak(timer: MacroTimer): MacroTimer {
  if (timer.state !== 'break_running') return timer;
  const t = now();
  const delta = timer.breakStartedAt ? t - timer.breakStartedAt : 0;
  return {
    ...timer,
    state: 'break_paused',
    breakAccumulatedMs: timer.breakAccumulatedMs + delta,
    breakStartedAt: null,
    pausedAt: t
  };
}

export function resumeBreak(timer: MacroTimer): MacroTimer {
  if (timer.state !== 'break_paused') return timer;
  return {
    ...timer,
    state: 'break_running',
    breakStartedAt: now(),
    pausedAt: null
  };
}

export function requestFinish(timer: MacroTimer): MacroTimer {
  if (timer.state === 'inactive' || timer.state === 'finished' || timer.state === 'discarded') {
    return timer;
  }
  const t = now();
  let focusAccumulatedMs = timer.focusAccumulatedMs;
  let breakAccumulatedMs = timer.breakAccumulatedMs;
  if (timer.focusStartedAt !== null) {
    focusAccumulatedMs += t - timer.focusStartedAt;
  }
  if (timer.breakStartedAt !== null) {
    breakAccumulatedMs += t - timer.breakStartedAt;
  }
  return {
    ...timer,
    state: 'pending_finish',
    focusAccumulatedMs,
    breakAccumulatedMs,
    focusStartedAt: null,
    breakStartedAt: null,
    pausedAt: t
  };
}

export function confirmFinish(timer: MacroTimer): MacroTimer {
  if (timer.state !== 'pending_finish') return timer;
  return { ...timer, state: 'finished' };
}

export function resumeFromPending(timer: MacroTimer, previousState: MacroTimerState): MacroTimer {
  if (timer.state !== 'pending_finish') return timer;
  return { ...timer, state: previousState };
}

export function discardSession(timer: MacroTimer): MacroTimer {
  return { ...timer, state: 'discarded' };
}

export function elapsedFocusMs(timer: MacroTimer): number {
  const live = timer.focusStartedAt ? now() - timer.focusStartedAt : 0;
  return timer.focusAccumulatedMs + live;
}

export function elapsedBreakMs(timer: MacroTimer): number {
  const live = timer.breakStartedAt ? now() - timer.breakStartedAt : 0;
  return timer.breakAccumulatedMs + live;
}

export function remainingFocusMs(timer: MacroTimer): number {
  const used = elapsedFocusMs(timer);
  return Math.max(0, timer.focusDurationMs - used);
}

export function remainingBreakMs(timer: MacroTimer): number {
  const used = elapsedBreakMs(timer);
  return Math.max(0, timer.breakDurationMs - used);
}

export function addReadingTime(timer: MacroTimer, ms: number): MacroTimer {
  return { ...timer, readingAccumulatedMs: timer.readingAccumulatedMs + ms };
}

export function addDistraction(timer: MacroTimer): MacroTimer {
  return { ...timer, distractions: timer.distractions + 1 };
}

export function tickFocus(timer: MacroTimer): TickResult {
  if (timer.state !== 'focus_running') return { timer, transitionedTo: null };
  if (timer.openEnded) return { timer, transitionedTo: null };
  if (remainingFocusMs(timer) > 0) return { timer, transitionedTo: null };
  if (timer.breakDurationMs > 0) {
    return { timer: startBreak(timer), transitionedTo: 'break_running' };
  }
  return { timer: requestFinish(timer), transitionedTo: 'pending_finish' };
}

export function tickBreak(timer: MacroTimer): TickResult {
  if (timer.state !== 'break_running') return { timer, transitionedTo: null };
  if (remainingBreakMs(timer) > 0) return { timer, transitionedTo: null };
  return { timer: startFocus(timer), transitionedTo: 'focus_running' };
}